import { useState, useEffect, useCallback } from "react";
import { Building2, LogOut, Menu, X, BarChart3, ClipboardList, ShieldCheck, FileText, Users, Shield, FileBarChart, CalendarDays, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import OrganizationsManagement from "../components/OrganizationsManagement";
import SuperAdminDashboard from "../components/SuperAdminDashboard";
import ActivityLog from "../components/ActivityLog";
import MasterHealthPanel from "../components/MasterHealthPanel";
import OnboardingIntake from "../components/OnboardingIntake";
import SuperAdminTeamTab from "../components/SuperAdminTeamTab";
import SuperAdminRolesTab from "../components/SuperAdminRolesTab";
import SuperAdminReportsTab from "../components/SuperAdminReportsTab";
import SuperAdminBookingsTab from "../components/SuperAdminBookingsTab";
import SuperAdminDemoTab from "../components/SuperAdminDemoTab";
import { BACKEND_URL } from "../config/api";
import { getAdminUserData, canReadTab, logAdminEvent } from "../utils/superAdmin";

const SuperAdminDashboardV2 = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(getAdminUserData());
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem("superAdminActiveTab") || "dashboard";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    localStorage.setItem("superAdminActiveTab", activeView);
  }, [activeView]);

  // Global session-expiry guard: any 401 from super-admin APIs → back to login
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const url = error.config?.url || '';
        if (error.response?.status === 401 && url.includes('/v2/super-admin')) {
          localStorage.removeItem("jwtToken");
          localStorage.removeItem("userData");
          toast.error("Session expired — please sign in again");
          navigate("/platform-admin");
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);

  // Refresh permissions from the server (role changes take effect on reload)
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        const res = await axios.get(`${BACKEND_URL}/api/v2/super-admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const me = res.data;
        const fresh = {
          username: me.username,
          name: me.name,
          role_name: me.role_name,
          tab_permissions: me.tab_permissions || {},
          scope_own_orgs: me.scope_own_orgs || false,
          is_owner_role: me.is_owner_role || false,
          has_pin: me.has_pin || false,
        };
        localStorage.setItem("userData", JSON.stringify(fresh));
        setUserData(fresh);
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("jwtToken");
          navigate("/platform-admin");
        }
      }
    };
    fetchMe();
  }, [navigate]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        const response = await axios.get(`${BACKEND_URL}/api/v2/super-admin/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const orgs = response.data.organizations || response.data || [];
        setPendingCount(orgs.filter(org => org.status === 'pending').length);
      } catch (error) {
        // scoped/limited roles may not have accounts access
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logAdminEvent("logout", {});
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("userType");
    localStorage.removeItem("userData");
    toast.success("Logged out successfully");
    navigate("/platform-admin");
  };

  const allMenuItems = [
    { id: "dashboard", label: "Overview", icon: BarChart3 },
    { id: "accounts", label: "Accounts", icon: Building2, badge: pendingCount },
    { id: "bookings", label: "Bookings", icon: CalendarDays },
    { id: "team", label: "Team", icon: Users },
    { id: "roles", label: "Roles", icon: Shield },
    { id: "reports", label: "Reports", icon: FileBarChart },
    { id: "onboarding", label: "Onboarding", icon: FileText },
    { id: "demo", label: "Demo", icon: KeyRound },
    { id: "activity", label: "Activity", icon: ClipboardList },
    { id: "daily-tests", label: "Daily Tests", icon: ShieldCheck },
  ];

  const menuItems = allMenuItems.filter(item => canReadTab(userData, item.id));

  // If the saved tab is no longer permitted, fall back to the first visible tab
  useEffect(() => {
    if (menuItems.length > 0 && !menuItems.some(m => m.id === activeView)) {
      setActiveView(menuItems[0].id);
    }
  }, [userData]); // eslint-disable-line

  const selectTab = useCallback((id) => {
    setActiveView(id);
    logAdminEvent("tab_viewed", { tab: id });
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }, []);

  return (
    <div className="flex h-screen font-['Poppins',sans-serif] bg-white">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — same layout language as the operator dashboard */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0 lg:w-64'
        } bg-[#FBFCFF] border-r border-slate-200/60 fixed lg:relative transition-all duration-300 flex flex-col h-full z-50 overflow-hidden max-w-[16rem]`}
        style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}
      >
        {/* Sidebar Header */}
        <div className="px-4 pt-6 pb-4 relative">
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-1 right-1 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 transition-colors hover:bg-slate-200"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2.5 min-w-0">
            <img
              src="/logo-icon.png"
              alt="BookingFlows"
              className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
            />
            <span className="flex flex-col items-start leading-none min-w-0">
              <span className="font-bold text-sm tracking-tight text-slate-900 truncate w-full">
                BookingFlows
              </span>
              <span className="text-[9px] font-semibold text-slate-400 tracking-wide mt-0.5">Platform Admin</span>
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto scrollbar-hide">
          <div className="px-4 pt-1 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">
            Platform
          </div>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;

            return (
              <button
                key={item.id}
                data-testid={`superadmin-tab-${item.id}`}
                onClick={() => selectTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-left transition-colors duration-150 ${
                  isActive
                    ? 'bg-[#EBF2FF] text-[#2563EB] font-bold'
                    : 'text-slate-700 hover:bg-slate-100/80 font-semibold'
                }`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-150 ${
                  isActive ? 'bg-[#2563EB] text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <span className="truncate">{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom: signed-in + logout */}
        <div className="px-3 pb-4 pt-3 flex-shrink-0 space-y-1 border-t border-slate-100/70">
          <p className="px-3 py-1 text-xs font-semibold text-slate-400 truncate" data-testid="superadmin-signed-in-as">
            {userData.name || userData.username || "Super Admin"}
            {userData.role_name ? ` • ${userData.role_name}` : ""}
          </p>
          <button
            onClick={handleLogout}
            data-testid="superadmin-logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors duration-150"
          >
            <span className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden bg-white">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center gap-3 px-4 h-14 border-b border-gray-100 flex-shrink-0 bg-white">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="superadmin-mobile-menu"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            {activeView === "dashboard" && <SuperAdminDashboard />}
            {activeView === "accounts" && <OrganizationsManagement />}
            {activeView === "bookings" && <SuperAdminBookingsTab />}
            {activeView === "team" && <SuperAdminTeamTab />}
            {activeView === "roles" && <SuperAdminRolesTab />}
            {activeView === "reports" && <SuperAdminReportsTab />}
            {activeView === "onboarding" && <OnboardingIntake />}
            {activeView === "demo" && <SuperAdminDemoTab />}
            {activeView === "activity" && <ActivityLog />}
            {activeView === "daily-tests" && <MasterHealthPanel />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SuperAdminDashboardV2;
